import { Link } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { ShoppingCart, ArrowRight, Truck, Thermometer, MapPin, CreditCard, PackageSearch } from 'lucide-react'
import Button from '../components/ui/Button'
import WishlistButton from '../components/product/WishlistButton'
import { Product } from '../types'
import { getActiveProducts } from '../lib/products'
import { useCartStore } from '../store/cartStore'

const IMAGE_FALLBACK =
    'https://images.unsplash.com/photo-1571115764595-644a1f56a55c?w=600&h=450&fit=crop'

export default function HomePage() {
    const [featuredProducts, setFeaturedProducts] = useState<Product[]>([])
    const [otherProducts, setOtherProducts] = useState<Product[]>([])
    const [loading, setLoading] = useState(true)
    const addItem = useCartStore(state => state.addItem)

    useEffect(() => {
        fetchProducts()
    }, [])

    const fetchProducts = async () => {
        try {
            // Tek sorgu ile tüm aktif ürünleri getir, öne çıkanları istemcide ayır
            const allProducts = await getActiveProducts()
            setFeaturedProducts(allProducts.filter(p => p.isFeatured))
            setOtherProducts(allProducts.filter(p => !p.isFeatured))
        } catch (error) {
            console.error('Ürünler yüklenirken hata:', error)
            setFeaturedProducts([])
            setOtherProducts([])
        } finally {
            setLoading(false)
        }
    }

    const handleAddToCart = (product: Product) => {
        try {
            addItem(product, 1)
        } catch (error) {
            console.error('Sepete eklenirken hata:', error)
        }
    }

    const formatPrice = (price: number) =>
        new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY',
        }).format(price)

    const ProductCard = ({ product, badge }: { product: Product; badge?: string }) => (
        <div className="group flex flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white transition-shadow duration-300 hover:shadow-lg">
            <div className="relative aspect-[4/3] overflow-hidden bg-neutral-100">
                <img
                    src={product.image || IMAGE_FALLBACK}
                    alt={product.name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                {badge && (
                    <span className="absolute left-3 top-3 rounded-full bg-primary-600 px-3 py-1 text-xs font-medium text-white">
                        {badge}
                    </span>
                )}
                <div className="absolute right-3 top-3">
                    <WishlistButton product={product} />
                </div>
            </div>

            <div className="flex flex-1 flex-col p-5">
                <Link to={`/urun/${product.id}`}>
                    <h3 className="mb-1 line-clamp-1 text-base font-semibold text-neutral-900 transition-colors group-hover:text-primary-700">
                        {product.name}
                    </h3>
                </Link>
                <p className="mb-4 line-clamp-2 text-sm leading-relaxed text-neutral-500">
                    {product.description}
                </p>

                <div className="mt-auto flex items-center justify-between">
                    <div>
                        <span className="text-lg font-semibold text-neutral-900">
                            {formatPrice(product.price)}
                        </span>
                        <span className="ml-1 text-xs text-neutral-400">KDV dahil</span>
                    </div>
                    <Button
                        size="sm"
                        className="gap-2"
                        onClick={() => handleAddToCart(product)}
                    >
                        <ShoppingCart className="h-4 w-4" />
                        Sepete Ekle
                    </Button>
                </div>
            </div>
        </div>
    )

    const CardSkeleton = () => (
        <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white">
            <div className="aspect-[4/3] animate-pulse bg-neutral-100" />
            <div className="space-y-3 p-5">
                <div className="h-4 w-2/3 animate-pulse rounded bg-neutral-100" />
                <div className="h-3 w-full animate-pulse rounded bg-neutral-100" />
                <div className="h-3 w-1/2 animate-pulse rounded bg-neutral-100" />
            </div>
        </div>
    )

    return (
        <>
            {/* HERO */}
            <section className="relative flex min-h-[88vh] items-center overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <video
                        autoPlay
                        muted
                        loop
                        playsInline
                        poster="/hero.webp"
                        className="h-full w-full object-cover"
                    >
                        <source src="/hero.mp4" type="video/mp4" />
                    </video>
                    <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
                </div>

                <div className="relative z-10 mx-auto w-full max-w-5xl px-4 py-24 text-center sm:px-6">
                    <p className="mb-4 text-sm font-medium uppercase tracking-[0.2em] text-white/80">
                        Küçükçekmece, İstanbul
                    </p>
                    <h1 className="text-4xl font-bold leading-tight text-white sm:text-5xl md:text-6xl">
                        Geleneksel börek, <br className="hidden sm:block" />
                        her gün taze.
                    </h1>
                    <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-white/85 sm:text-lg">
                        El açması hamur ve özenle hazırlanan iç harçlarla, börek ve baklavayı
                        sıcacık kapınıza getiriyoruz.
                    </p>

                    <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
                        <Link to="/urunler" className="w-full sm:w-auto">
                            <Button size="lg" className="w-full gap-2 sm:w-auto">
                                Ürünleri İncele
                                <ArrowRight className="h-4 w-4" />
                            </Button>
                        </Link>
                        <Link to="/siparis-takip" className="w-full sm:w-auto">
                            <button className="w-full rounded-lg border border-white/40 bg-white/10 px-6 py-3 text-base font-medium text-white backdrop-blur-sm transition-colors hover:bg-white/20 sm:w-auto">
                                Siparişimi Takip Et
                            </button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* ÖNE ÇIKANLAR */}
            <section className="bg-neutral-50 py-20">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="mb-12 max-w-2xl">
                        <p className="mb-2 text-sm font-medium uppercase tracking-wider text-primary-600">
                            Öne Çıkanlar
                        </p>
                        <h2 className="text-3xl font-bold text-neutral-900 sm:text-4xl">
                            Sofranıza yakışan lezzetler
                        </h2>
                        <p className="mt-3 text-base leading-relaxed text-neutral-500">
                            En çok tercih edilen börek ve baklava çeşitlerimizden bir seçki.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {loading
                            ? Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)
                            : featuredProducts.map(product => (
                                  <ProductCard key={product.id} product={product} badge="Öne Çıkan" />
                              ))}
                    </div>

                    {!loading && featuredProducts.length === 0 && (
                        <p className="py-12 text-center text-neutral-500">
                            Şu anda öne çıkan ürün bulunmuyor.
                        </p>
                    )}
                </div>
            </section>

            {/* TÜM ÜRÜNLER */}
            {(loading || otherProducts.length > 0) && (
                <section className="bg-white py-20">
                    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                        <div className="mb-12 max-w-2xl">
                            <p className="mb-2 text-sm font-medium uppercase tracking-wider text-primary-600">
                                Menümüz
                            </p>
                            <h2 className="text-3xl font-bold text-neutral-900 sm:text-4xl">
                                Tüm lezzetlerimiz
                            </h2>
                            <p className="mt-3 text-base leading-relaxed text-neutral-500">
                                Su böreğinden baklavaya, her damak zevkine uygun çeşitlerimiz.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                            {loading
                                ? Array.from({ length: 8 }).map((_, i) => <CardSkeleton key={i} />)
                                : otherProducts.map(product => (
                                      <ProductCard key={product.id} product={product} />
                                  ))}
                        </div>

                        <div className="mt-12 text-center">
                            <Link to="/urunler">
                                <Button variant="outline" size="lg" className="gap-2">
                                    Tüm Ürünleri Gör
                                    <ArrowRight className="h-4 w-4" />
                                </Button>
                            </Link>
                        </div>
                    </div>
                </section>
            )}

            {/* TESLİMAT */}
            <section className="bg-neutral-50 py-20">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
                        <div className="relative order-2 aspect-[4/3] w-full overflow-hidden rounded-2xl bg-neutral-200 lg:order-1">
                            <img
                                src="/images/kurye.jpg"
                                alt="Petunya Börek teslimat aracı"
                                loading="lazy"
                                className="absolute inset-0 h-full w-full object-cover"
                            />
                        </div>

                        <div className="order-1 lg:order-2">
                            <p className="mb-2 text-sm font-medium uppercase tracking-wider text-primary-600">
                                Teslimat
                            </p>
                            <h2 className="text-3xl font-bold text-neutral-900 sm:text-4xl">
                                Kapınıza sıcak ve zamanında
                            </h2>
                            <p className="mt-3 max-w-lg text-base leading-relaxed text-neutral-500">
                                İstanbul Avrupa Yakası'na kendi araçlarımızla teslimat yapıyoruz.
                                Teslimat süresi bölgeye göre değişiklik gösterir.
                            </p>

                            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
                                <Feature
                                    icon={<Thermometer className="h-5 w-5" />}
                                    title="Sıcaklık korumalı taşıma"
                                    text="Termal çantalarla böreğiniz sıcak ulaşır."
                                />
                                <Feature
                                    icon={<PackageSearch className="h-5 w-5" />}
                                    title="Canlı sipariş takibi"
                                    text="Siparişinizin durumunu anlık görüntüleyin."
                                />
                                <Feature
                                    icon={<CreditCard className="h-5 w-5" />}
                                    title="Kapıda güvenli ödeme"
                                    text="Nakit veya POS ile kapıda ödeyin."
                                />
                                <Feature
                                    icon={<MapPin className="h-5 w-5" />}
                                    title="Avrupa Yakası teslimat"
                                    text="Bölgenizi sipariş adımında kontrol edin."
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-primary-700 py-16">
                <div className="mx-auto max-w-4xl px-4 text-center sm:px-6">
                    <div className="mb-5 flex justify-center">
                        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/15">
                            <Truck className="h-6 w-6 text-white" />
                        </span>
                    </div>
                    <h2 className="text-3xl font-bold text-white sm:text-4xl">
                        Bugün taze böreğin keyfini çıkarın
                    </h2>
                    <p className="mx-auto mt-4 max-w-xl text-base text-white/85">
                        Siparişinizi verin, gerisini biz halledelim.
                    </p>
                    <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                        <Link to="/urunler" className="w-full sm:w-auto">
                            <button className="w-full rounded-lg bg-white px-8 py-3 text-base font-semibold text-primary-700 transition-colors hover:bg-neutral-100 sm:w-auto">
                                Sipariş Ver
                            </button>
                        </Link>
                        <Link
                            to="/siparis-takip"
                            className="inline-flex items-center gap-2 px-4 py-3 text-base font-medium text-white/90 transition-colors hover:text-white"
                        >
                            Siparişimi Takip Et
                            <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                </div>
            </section>
        </>
    )
}

function Feature({
    icon,
    title,
    text,
}: {
    icon: React.ReactNode
    title: string
    text: string
}) {
    return (
        <div className="flex gap-3">
            <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                {icon}
            </span>
            <div>
                <h3 className="font-semibold text-neutral-900">{title}</h3>
                <p className="mt-0.5 text-sm leading-relaxed text-neutral-500">{text}</p>
            </div>
        </div>
    )
}
